/**
 * PO Ledger（PO台帳）— Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/openspec/changes/po-ledger-foundation/` の
 * PR4（QueryService + API 層）はまだ実装されていない（Phase 1 実装時点で BE API は 0 本）。
 * `@standandforce/api-contract` に `poLedgerContract` が publish されるのは BE PR4 マージ後。
 * それまでは item-ledger の先例（`./item-ledger.ts`）に倣い、ローカルに最低限の Contract を定義する。
 * publish 後は client.ts のローカル import を置き換え、本ファイルは削除する想定。
 *
 * 値域・列定義の出典:
 * - openspec/changes/po-ledger-foundation/specs/po-ledger-list/spec.md（一覧 API 契約）
 * - openspec/changes/po-ledger-foundation/specs/po-ledger-detail/spec.md（詳細 API 契約）
 * - openspec/changes/po-ledger-foundation/design.md D13〜D15
 *
 * 詳細画面の基本情報タブ（6セクション）は Section/Attribute SDUI（独自キー方式）へ移行済み
 * （Permission&Role のカテゴリ権限制御を対象外とする方針により graph-to-code / CategoryFieldAccess には
 * 非連携。customer-ledger / item-ledger と同じ独自キー方式）。発注明細タブ（1:N構造）と
 * amounts（本文セクションを持たずヘッダのサマリ表示にのみ使用）は対象外で従来どおり flat。
 *
 * 写経元は BE の
 * `dresscode-backend/v2/src/prc-force/api/po-ledger/usecase/po-ledger.api-contract.ts`。
 * 差分は日付型のみ（BE `z.date()` → FE `z.coerce.date()`。理由は下記 Date の節）。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginationQuerySchema } from './pagination';

// ─── Enum values（design.md D15 / specs/po-ledger-list と同期） ───

/** Po Status（11種、概念ADR §2-1 / design.md D15-⑩）。Rejected・Closed は含まない。 */
export const poStatusValues = ['DRAFT', 'SUBMITTED', 'APPROVED', 'RELEASED', 'OPEN', 'CONFIRMED', 'CHANGE_REQUESTED', 'EXPIRED', 'ON_HOLD', 'CANCELLED', 'VOIDED'] as const;
export type PoStatus = (typeof poStatusValues)[number];

/** Po Type（契約種別、分類軸①）。design.md D15-②。 */
export const poTypeValues = ['SPOT', 'CONTRACT_BASED', 'BLANKET_RELEASE', 'FRAME_RELEASE'] as const;
export type PoType = (typeof poTypeValues)[number];

/** Po Subtype（購入品目、分類軸②、複数値）。design.md D15-②。 */
export const poSubtypeValues = ['GOODS', 'SERVICES', 'CAPITAL_ASSET'] as const;
export type PoSubtype = (typeof poSubtypeValues)[number];

/** Po Mode（発注モード、分類軸③、一覧列にはせずフィルタのみ）。design.md D15-②。 */
export const poModeValues = ['NORMAL', 'URGENT', 'EMERGENCY', 'DIRECT_ORDER'] as const;
export type PoMode = (typeof poModeValues)[number];

/** Procurement Category（調達カテゴリー、分類軸④、一覧列にはせずフィルタのみ）。design.md D15-②。 */
export const procurementCategoryValues = ['DIRECT', 'INDIRECT', 'CAPITAL', 'MRO', 'SERVICES', 'IT'] as const;
export type ProcurementCategory = (typeof procurementCategoryValues)[number];

/** 優先度。fe-impl-plan.md §7「その他の辞書対象」。 */
export const priorityValues = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as const;
export type Priority = (typeof priorityValues)[number];

// ─── DataGrid Response Schema（item-ledger と同形） ───

const DataGridColumnSchema = z.object({
  fieldKey: z.string(),
  fieldName: z.string(),
  cellType: z.string(),
  sortable: z.boolean(),
  pinned: z.string().nullable(),
  minWidth: z.string().nullable()
});
const DataGridSchemaSchema = z.object({
  columns: z.array(DataGridColumnSchema),
  defaultSort: z.object({
    fieldKey: z.string(),
    direction: z.enum(['asc', 'desc'])
  }).nullable()
});
const DataGridItemSchema = z.object({
  id: z.string(),
  fieldValues: z.record(z.string(), z.unknown())
});
const DataGridResponseSchema = z.object({
  schema: DataGridSchemaSchema,
  items: z.array(DataGridItemSchema),
  count: z.number(),
  page: z.number(),
  maxPage: z.number()
});

// ─── Input Schema（一覧、specs/po-ledger-list「一覧の検索・フィルタ」） ───

// orderBy は DataGrid の fieldKey を受け取り、BE 側で sortKey に解決する（item-ledger と同様）。
const PoListInputSchema = paginationQuerySchema(z.string()).extend({
  searchText: z.string().nullable().default(null),
  // 基本フィルタ（ツールバー）
  statuses: z.array(z.enum(poStatusValues)).nullable().default(null),
  deliveryDueDateFrom: z.string().nullable().default(null),
  deliveryDueDateTo: z.string().nullable().default(null),
  // 分類軸フィルタ（詳細フィルタパネル）
  priorities: z.array(z.enum(priorityValues)).nullable().default(null),
  poTypes: z.array(z.enum(poTypeValues)).nullable().default(null),
  orderDateFrom: z.string().nullable().default(null),
  orderDateTo: z.string().nullable().default(null),
  poSubtypes: z.array(z.enum(poSubtypeValues)).nullable().default(null),
  poModes: z.array(z.enum(poModeValues)).nullable().default(null),
  procurementCategories: z.array(z.enum(procurementCategoryValues)).nullable().default(null)
});

// ─── Summary Card（件数バー、specs/po-ledger-list「PO一覧のステータス別件数」） ───

const PoSummaryCardOutputSchema = z.object({
  DRAFT: z.number(),
  SUBMITTED: z.number(),
  APPROVED: z.number(),
  RELEASED: z.number(),
  OPEN: z.number(),
  CONFIRMED: z.number(),
  CHANGE_REQUESTED: z.number(),
  EXPIRED: z.number(),
  ON_HOLD: z.number(),
  CANCELLED: z.number(),
  VOIDED: z.number()
});

// ─── 詳細（design.md「Read Model 定義」`po_detail` / specs/po-ledger-detail） ───
//
// Date の扱い: BE 契約は `z.date()` だが oRPC は JSON transport のため BE の Date は
// ISO 文字列として届く。`z.date()` は文字列を受け付けないので `z.coerce.date()` で
// ISO → Date に変換する（item-ledger のローカル Contract と同じ規約）。

const PoIdInputSchema = z.object({
  poId: z.string()
});

/**
 * Line Status の表示値域（design.md D9 の5値から INVOICED を除いた4値）。
 * INVOICED は「保有と表示の分離」により FULLY_RECEIVED へ読み替えて返すため出力に現れない。
 * null は「合意前（履行軸が存在しない）」の予約値で、表示は「—」。
 */
const poLineStatusDisplayValues = ['NOT_RECEIVED', 'PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'CANCELLED'] as const;
export type PoLineStatusDisplay = (typeof poLineStatusDisplayValues)[number];

/** 取引通貨（既存 Currency enum 6通貨）。換算はしない（design.md D14）。 */
const poCurrencyValues = ['JPY', 'USD', 'IDR', 'THB', 'VND', 'CNY'] as const;

/**
 * ヘッダサマリ（Header 導出4項目 + 最新版フラグ）。
 * 承認・合意イベント未経験の PO では導出項目が null になり、FE は「—」表示にフォールバックする。
 * `deliveryDueDate` はヘッダのサマリ表示（納入期限）専用に持つ（取引条件タブは `TERMS`
 * セクションの `DELIVERY_DUE_DATE` 属性が別途保持し、ヘッダ表示が body セクション内部に
 * 依存しないよう独立して返る）。
 */
const PoDetailHeaderSchema = z.object({
  poId: z.string(),
  poNumber: z.string(),
  poName: z.string().nullable(),
  poStatus: z.enum(poStatusValues),
  /** 表示用の版番号 `v{External}.{Internal}` */
  versionNumber: z.string().nullable(),
  approver: z.string().nullable(),
  approvedAt: z.coerce.date().nullable(),
  receivedAt: z.coerce.date().nullable(),
  deliveryDueDate: z.coerce.date().nullable(),
  isLatestVersion: z.boolean()
});

/**
 * 基本情報タブ6セクション（識別・ステータス / 仕入先 / 仕入先担当者連絡先 / 発注担当 /
 * 取引条件 / 会計・予算配賦）の Section/Attribute SDUI（独自キー方式）。
 * customer-ledger / item-ledger と同じ契約形（BE `po-ledger.api-contract.ts` 写し）。
 */
const PoSectionKeyValues = ['IDENTIFICATION', 'SUPPLIER', 'SUPPLIER_CONTACT', 'ORDERING', 'TERMS', 'ACCOUNTING'] as const;
const PoAttributeOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  descriptionLabel: z.string().nullish()
});
const PoAttributeSchema = z.object({
  attributeKey: z.string(),
  label: z.string(),
  valueType: z.string(),
  value: z.unknown().nullable(),
  readonly: z.boolean(),
  required: z.boolean(),
  options: z.array(PoAttributeOptionSchema).optional(),
  displayOrder: z.number()
});
const PoDetailSectionSchema = z.object({
  sectionKey: z.enum(PoSectionKeyValues),
  name: z.string(),
  displayOrder: z.number(),
  attributes: z.array(PoAttributeSchema)
});

/** ヘッダ金額4値（design.md D15-⑨）。生値 + 通貨コードで返り整形は FE の責務（design.md D14） */
const PoDetailAmountsSchema = z.object({
  subtotalExclTax: z.number().nullable(),
  totalTax: z.number().nullable(),
  costAdjustment: z.number().nullable(),
  grandTotalInclTax: z.number().nullable(),
  currencyCode: z.enum(poCurrencyValues).nullable()
});

/**
 * 明細進捗（照合軸8項目 + Line Status — design.md「Read Model 定義」`po_line_progress`）。
 * 実績由来（受入済・検収済・請求受取済）は外部イベント購読が未実装のため Phase 1 は常に 0。
 */
const PoLineProgressSchema = z.object({
  orderedQty: z.number(),
  supplierConfirmedQty: z.number().nullable(),
  receivedQty: z.number(),
  remainingReceiptQty: z.number(),
  acceptedQty: z.number(),
  invoicedQty: z.number(),
  remainingInvoiceQty: z.number(),
  openOrderQty: z.number(),
  lineStatus: z.enum(poLineStatusDisplayValues).nullable()
});

/** 明細（明細番号順。8列表 + ドロワー用の進捗 — fe-impl-plan §2-2 / §6-5） */
const PoDetailLineSchema = z.object({
  poLineId: z.string(),
  lineNo: z.number(),
  itemId: z.string().nullable(),
  itemCode: z.string().nullable(),
  itemName: z.string().nullable(),
  poSubtype: z.enum(poSubtypeValues).nullable(),
  procurementCategory: z.enum(procurementCategoryValues).nullable(),
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
  netUnitPrice: z.number().nullable(),
  lineAmountExclTax: z.number().nullable(),
  promisedDeliveryDate: z.coerce.date().nullable(),
  progress: PoLineProgressSchema.nullable()
});

/**
 * バージョン履歴（po_version）。Phase 1 は駆動イベントが本番発行されないため実データは空。
 * 版間の差分算出は FE のレンダー時責務（差分算出 API は提供されない — specs/po-ledger-detail）。
 */
const PoVersionSchema = z.object({
  poVersionId: z.string(),
  versionExternal: z.number(),
  versionInternal: z.number(),
  /** 表示用 `v{External}.{Internal}` */
  versionNumber: z.string(),
  /** 生成契機のイベント種別（PO_APPROVED / PO_CONFIRMED） */
  type: z.string(),
  /** 凍結内容。po_snapshot と同じ header / lines / documents の3スライス構造 */
  snapshotData: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date()
});

// 従属子4種はテーブル定義のみで行実装が後続のため、Phase 1 の詳細 API は常に空配列を返す
// （design.md「Read Model 定義」）。FE も Phase 1 ではセクションを描画しない
// （fe-impl-plan.md §2-2「従属子」）が、型の欠落で写経漏れを起こさないよう契約には持つ。

const PoDeliveryScheduleSchema = z.object({
  poDeliveryScheduleId: z.string(),
  poLineId: z.string(),
  scheduleNo: z.number(),
  scheduledDate: z.coerce.date().nullable(),
  scheduledQty: z.number().nullable(),
  receivedQty: z.number().nullable()
});
const PoAccountingAllocationSchema = z.object({
  poAccountingAllocationId: z.string(),
  poLineId: z.string(),
  allocationNo: z.number(),
  allocationRatio: z.number().nullable(),
  allocationAmount: z.number().nullable(),
  budgetId: z.string().nullable(),
  costCenterId: z.string().nullable(),
  profitCenterId: z.string().nullable(),
  glAccountCode: z.string().nullable()
});
const PoLineDocumentSchema = z.object({
  poLineDocumentId: z.string(),
  poLineId: z.string(),
  documentId: z.string(),
  documentType: z.string().nullable()
});
const PoPaymentScheduleSchema = z.object({
  poPaymentScheduleId: z.string(),
  /** null = PO 全体に適用（従属子で唯一のヘッダ直下 1:N） */
  poLineId: z.string().nullable(),
  lineNo: z.number(),
  paymentRatio: z.number().nullable(),
  paymentAmount: z.number().nullable(),
  paymentDueOn: z.coerce.date().nullable(),
  milestoneId: z.string().nullable(),
  milestoneName: z.string().nullable(),
  paymentStatus: z.string().nullable()
});
const PoDetailOutputSchema = z.object({
  header: PoDetailHeaderSchema,
  sections: z.array(PoDetailSectionSchema),
  amounts: PoDetailAmountsSchema,
  lines: z.array(PoDetailLineSchema),
  deliverySchedules: z.array(PoDeliveryScheduleSchema),
  accountingAllocations: z.array(PoAccountingAllocationSchema),
  lineDocuments: z.array(PoLineDocumentSchema),
  paymentSchedules: z.array(PoPaymentScheduleSchema),
  versions: z.array(PoVersionSchema)
});
export type PoDetailHeader = z.infer<typeof PoDetailHeaderSchema>;
export type PoAttribute = z.infer<typeof PoAttributeSchema>;
export type PoDetailSection = z.infer<typeof PoDetailSectionSchema>;
export type PoDetailAmounts = z.infer<typeof PoDetailAmountsSchema>;
export type PoDetailLine = z.infer<typeof PoDetailLineSchema>;
export type PoLineProgress = z.infer<typeof PoLineProgressSchema>;

// ─── Contract ───

export const poLedgerContract = oc.router({
  poList: oc.route({
    method: 'GET',
    path: '/v2/prc-force/po-ledger/pos'
  }).input(PoListInputSchema).output(DataGridResponseSchema),
  poSummaryCard: oc.route({
    method: 'GET',
    path: '/v2/prc-force/po-ledger/pos/summary-card'
  }).input(z.object({})).output(PoSummaryCardOutputSchema),
  poDetail: oc.route({
    method: 'GET',
    path: '/v2/prc-force/po-ledger/pos/{poId}'
  }).input(PoIdInputSchema).output(PoDetailOutputSchema)
});